import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { formatINR } from "../lib/api";
import { Search, X } from "lucide-react";

export default function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (open) { setTimeout(() => inputRef.current?.focus(), 80); document.body.style.overflow = "hidden"; }
    else { document.body.style.overflow = ""; setQ(""); setResults(null); }
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults(null); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get("/search", { params: { q: q.trim() } });
        setResults(r.data);
      } catch { setResults({ products: [], categories: [], suggestions: [], zero: true }); }
      setLoading(false);
    }, 250);
  }, [q]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80]" role="dialog" aria-modal="true" aria-label="Search products">
      <div className="absolute inset-0 bg-charcoal/40" onClick={onClose} />
      <div className="relative bg-cream max-h-full overflow-y-auto shadow-2xl">
        <div className="max-w-3xl mx-auto px-5 py-6">
          <div className="flex items-center gap-3 bg-white rounded-full bg-[#EDEDED] px-5 py-3.5">
            <Search className="w-5 h-5 text-charcoal/50 shrink-0" />
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input"
              className="flex-1 bg-transparent outline-none text-base" placeholder="Search bottles, bento, pens, trolleys…" aria-label="Search products" />
            {loading && <span className="text-xs text-charcoal/40 animate-pulse">searching…</span>}
            <button onClick={onClose} aria-label="Close search" data-testid="search-close" className="p-1.5 rounded-full hover:bg-sage/40"><X className="w-5 h-5" /></button>
          </div>

          {results && (
            <div className="mt-6 space-y-6" data-testid="search-results">
              {results.categories?.length > 0 && (
                <section aria-label="Category results">
                  <h2 className="text-xs font-bold uppercase tracking-widest text-charcoal/50 mb-3">Categories</h2>
                  <div className="flex flex-wrap gap-2">
                    {results.categories.map((c) => (
                      <Link key={c.slug} to={`/collections/${c.slug}`} onClick={onClose} data-testid={`search-category-${c.slug}`}
                        className="border border-line bg-white px-4 py-2 rounded-full text-sm hover:border-charcoal transition-colors">
                        {c.name}
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {results.products?.length > 0 && (
                <section aria-label="Product results">
                  <h2 className="text-xs font-bold uppercase tracking-widest text-charcoal/50 mb-3">Products</h2>
                  <ul className="grid gap-2">
                    {results.products.map((p) => (
                      <li key={p.slug}>
                        <Link to={`/products/${p.slug}`} onClick={onClose} data-testid={`search-product-${p.slug}`}
                          className="flex items-center gap-4 bg-white rounded-2xl card-outline p-3 hover:bg-butter/20 transition-colors">
                          <img src={p.image} alt="" width="56" height="56" className="w-14 h-14 rounded-xl object-cover" loading="lazy" />
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate">{p.name}</p>
                            <p className="text-sm font-bold mt-0.5">{formatINR(p.price)}</p>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {results.zero && (
                <div className="bg-sky/30 rounded-3xl p-6 text-center" data-testid="search-zero-state">
                  <p className="font-display font-medium text-lg">No little joys matched "{q}"</p>
                  <p className="text-sm text-charcoal/70 mt-1">Try one of these instead:</p>
                  <div className="flex flex-wrap justify-center gap-2 mt-4">
                    {results.suggestions?.map((s) => (
                      <button key={s} onClick={() => setQ(s)} className="border border-line bg-white px-4 py-2 rounded-full text-sm hover:border-charcoal transition-colors">{s}</button>
                    ))}
                    <Link to="/collections/shop-all" onClick={onClose} className="bg-forest text-white px-4 py-2 rounded-full text-sm font-semibold">Browse everything</Link>
                  </div>
                </div>
              )}
            </div>
          )}

          {!results && (
            <p className="mt-6 text-sm text-charcoal/60">Search by product name, category, colour or theme — like "bento", "insulated" or "kawaii".</p>
          )}
        </div>
      </div>
    </div>
  );
}
